/**
 * Capacitor plugin wrapper for the Adjust analytics SDK.
 *
 * Adjust does not publish a Capacitor plugin. Their Cordova plugin
 * (https://github.com/adjust/cordova_sdk) installs the native SDK from CocoaPods,
 * which is incompatible with Swift Package Manager. This module wraps the native
 * SDK directly (AdjustPlugin.swift / AdjustPlugin.java) and exposes the parts
 * from the Adjust API we use: initSdk, trackEvent, getAdid, getIdfa, getGoogleAdId.
 *
 * It also includes a backward-compatible adapter (AdjustCordova)
 * It is used when the user's native app is older than the Capacitor plugin.
 */

import { registerPlugin } from '@capacitor/core'

export interface AdjustConfig {
  appToken: string
  environment: 'production' | 'sandbox'
  logLevel?: 'verbose' | 'debug' | 'info' | 'warn' | 'error' | 'suppress'
}

export interface AdjustEventOptions {
  eventToken: string
}

export interface CapacitorAdjustPlugin {
  initSdk(config: AdjustConfig): Promise<void>
  trackEvent(options: AdjustEventOptions): Promise<void>
  getAdid(): Promise<{ adid: string }>
  getIdfa(): Promise<{ idfa: string }>
  getGoogleAdId(): Promise<{ googleAdId: string }>
}

class AdjustCordova implements CapacitorAdjustPlugin {
  async initSdk(config: AdjustConfig): Promise<void> {
    // `window.Adjust` is the JS bridge injected by the Cordova Adjust plugin via
    // <clobbers target="Adjust"> in plugin.xml. This is not the Adjust web SDK.
    // window.Adjust.initSdk() serializes the config and calls
    // cordova.exec('Adjust', 'initSdk', ...) to the native iOS SDK.
    const AdjustConfigClass = (window as any).AdjustConfig
    const adjustConfig = new AdjustConfigClass(
      config.appToken,
      config.environment,
    )
    ;(window as any).Adjust.initSdk(adjustConfig)
  }

  async trackEvent(options: AdjustEventOptions): Promise<void> {
    const AdjustEventClass = (window as any).AdjustEvent
    const adjustEvent = new AdjustEventClass(options.eventToken)
    ;(window as any).Adjust.trackEvent(adjustEvent)
  }

  getAdid(): Promise<{ adid: string }> {
    return new Promise((resolve, reject) => {
      ;(window as any).cordova.exec(
        (adid: string) => resolve({ adid }),
        reject,
        'Adjust',
        'getAdid',
        [],
      )
    })
  }

  getIdfa(): Promise<{ idfa: string }> {
    return new Promise((resolve, reject) => {
      ;(window as any).cordova.exec(
        (idfa: string) => resolve({ idfa }),
        reject,
        'Adjust',
        'getIdfa',
        [],
      )
    })
  }

  getGoogleAdId(): Promise<{ googleAdId: string }> {
    return new Promise((resolve, reject) => {
      ;(window as any).cordova.exec(
        (googleAdId: string) => resolve({ googleAdId }),
        reject,
        'Adjust',
        'getGoogleAdId',
        [],
      )
    })
  }
}

class AdjustAdapter implements CapacitorAdjustPlugin {
  private instance?: CapacitorAdjustPlugin

  /**
   * Must be called once at startup before any other methods.
   * Uses feature detection to pick the Capacitor plugin (new app versions)
   * or the Cordova plugin (older app versions that predate the Capacitor plugin).
   */
  init(): void {
    const hasCapacitorPlugin = !!(window as any).Capacitor?.Plugins
      ?.CapacitorAdjust
    if (hasCapacitorPlugin) {
      this.instance = registerPlugin<CapacitorAdjustPlugin>('CapacitorAdjust')
    } else {
      this.instance = new AdjustCordova()
    }
  }

  async initSdk(config: AdjustConfig): Promise<void> {
    if (!this.instance) {
      throw new Error('AdjustAdapter: init() was not called before initSdk()')
    }
    return this.instance.initSdk(config)
  }

  async trackEvent(options: AdjustEventOptions): Promise<void> {
    if (!this.instance) {
      throw new Error(
        'AdjustAdapter: init() was not called before trackEvent()',
      )
    }
    return this.instance.trackEvent(options)
  }

  async getAdid(): Promise<{ adid: string }> {
    if (!this.instance) {
      throw new Error('AdjustAdapter: init() was not called before getAdid()')
    }
    return this.instance.getAdid()
  }

  async getIdfa(): Promise<{ idfa: string }> {
    if (!this.instance) {
      throw new Error('AdjustAdapter: init() was not called before getIdfa()')
    }
    return this.instance.getIdfa()
  }

  async getGoogleAdId(): Promise<{ googleAdId: string }> {
    if (!this.instance) {
      throw new Error(
        'AdjustAdapter: init() was not called before getGoogleAdId()',
      )
    }
    return this.instance.getGoogleAdId()
  }
}

export const CapacitorAdjust = new AdjustAdapter()
